import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Package, Search, Info, Truck, X, Plus } from "lucide-react";
import api, { formatApiError, downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/ui-helpers";
import { Input } from "@/components/ui/input";
import { CustomOrderDetail } from "@/components/CustomOrderDetail";
import { ManualOrderModal } from "@/components/ManualOrderModal";
import { CourierLogo } from "@/components/CourierLogo";

const BUCKETS = [
  ["new_orders", "New Orders"], ["pending_processing", "Pending Processing"], ["ready_to_ship", "Ready to Ship"],
  ["shipped", "Shipped"], ["in_transit", "In Transit"], ["delivered", "Delivered"],
  ["cancellation_requests", "Cancellation Reqs"], ["return_requests", "Return Reqs"], ["replacement_requests", "Replacement Reqs"],
  ["refund_pending", "Refund Pending"], ["completed", "Completed"], ["attention_required", "Attention Required"],
];
const STATUS_OPTS = ["all", "confirmed", "processing", "ready_to_ship", "shipped", "in_transit", "delivered", "completed", "return_requested", "replacement_requested", "refund_pending", "on_hold", "cancelled"];
const badge = (s) => s === "delivered" || s === "completed" ? "bg-emerald-100 text-emerald-700" : ["cancelled", "refunded", "returned"].includes(s) ? "bg-rose-100 text-rose-700" : s === "on_hold" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700";
const label = (s) => (s || "").replace(/_/g, " ");
const BUCKET_STATUS = { new_orders: "confirmed", pending_processing: "processing", ready_to_ship: "ready_to_ship", shipped: "shipped", in_transit: "in_transit", delivered: "delivered", completed: "completed", cancellation_requests: "cancellation_requested", return_requests: "return_requested", replacement_requests: "replacement_requested", refund_pending: "refund_pending" };
const HILITE_BUCKETS = ["return_requests", "replacement_requests"];

const CUSTOM_FLOW = ["processing", "ready", "shipped", "delivered", "completed"];
const CUSTOM_NEXT = { processing: "ready", ready: "shipped", shipped: "delivered", delivered: "completed" };
const CUSTOM_NEXT_LABEL = { ready: "Mark ready", shipped: "Mark shipped", delivered: "Mark delivered", completed: "Mark completed" };

export default function SellerOrders() {
  const nav = useNavigate();
  const [summary, setSummary] = useState({});
  const [orders, setOrders] = useState(null);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [est, setEst] = useState({});
  const [ship, setShip] = useState(null); // { sub, rates, sel, busy, loading, parcel }
  const [custom, setCustom] = useState([]);
  const [detail, setDetail] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const loadCustom = useCallback(async () => {
    const r = await api.get("/marketplace/seller/custom-orders").catch(() => ({ data: { orders: [] } }));
    const orders = r.data.orders || [];
    setCustom(orders);
    return orders;
  }, []);
  useEffect(() => { loadCustom(); }, [loadCustom]);
  const prevUnread = useRef(null);
  useEffect(() => {
    const tick = async () => {
      const orders = await loadCustom();
      const total = orders.reduce((a, o) => a + (o.unread || 0), 0);
      if (prevUnread.current !== null && total > prevUnread.current) toast.info("New message on a custom order");
      prevUnread.current = total;
    };
    const id = setInterval(tick, 20000);
    return () => clearInterval(id);
  }, [loadCustom]);
  const advanceCustom = async (r) => {
    const next = CUSTOM_NEXT[r.fulfilment_status || "processing"];
    if (!next) return;
    try {
      await api.post(`/marketplace/custom/${r.request_id}/fulfil`, { status: next });
      loadCustom();
      const undoable = next === "shipped" || next === "completed";
      toast.success(`Custom order marked ${next}`, undoable ? {
        duration: 5000,
        action: { label: "Undo", onClick: async () => {
          try { await api.post(`/marketplace/custom/${r.request_id}/fulfil/undo`); toast.success("Reverted"); loadCustom(); }
          catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        } },
      } : {});
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const SHIPPABLE = ["confirmed", "processing", "ready_to_ship"];
  const openShip = (o) => setShip({ sub: o, rates: null, sel: null, busy: false, loading: false, parcel: { weight: 0.5, length: 15, breadth: 12, height: 8 } });
  const shipRates = async () => {
    setShip((s) => ({ ...s, loading: true }));
    try {
      const p = ship.parcel;
      const r = await api.get(`/marketplace/seller/orders/${ship.sub.suborder_id}/rates`, { params: { weight: p.weight, length: p.length, breadth: p.breadth, height: p.height } });
      const cs = r.data.couriers || [];
      setShip((s) => ({ ...s, rates: cs, sel: cs[0]?.courier_id ?? null, loading: false }));
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); setShip((s) => ({ ...s, loading: false })); }
  };
  const doShip = async () => {
    const c = (ship.rates || []).find((x) => x.courier_id === ship.sel);
    if (!c) { toast.error("Get rates and select a courier first"); return; }
    setShip((s) => ({ ...s, busy: true }));
    try {
      const p = ship.parcel;
      const r = await api.post(`/marketplace/seller/orders/${ship.sub.suborder_id}/ship-shiprocket`, { courier_id: ship.sel, forward_charge: c.forward_charge, weight_kg: Number(p.weight), length_cm: Number(p.length), breadth_cm: Number(p.breadth), height_cm: Number(p.height) });
      toast.success(`Shipped · AWB ${r.data.awb || "assigned"}. Customer emailed with tracking.`);
      setShip(null); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); setShip((s) => ({ ...s, busy: false })); }
  };
  const shipSel = (ship?.rates || []).find((x) => x.courier_id === ship?.sel) || {};

  const estimate = async (sid) => {
    setEst((e) => ({ ...e, [sid]: { loading: true } }));
    try {
      const r = await api.get(`/marketplace/seller/orders/${sid}/rates`);
      const c = (r.data.couriers || [])[0];
      setEst((e) => ({ ...e, [sid]: c ? { fwd: c.forward_charge, ret: c.return_charge } : { none: true } }));
    } catch (err) {
      setEst((e) => ({ ...e, [sid]: { error: err?.response?.data?.detail || "N/A" } }));
    }
  };

  const load = useCallback(() => {
    api.get(`/marketplace/seller/orders?status=${status}&q=${encodeURIComponent(q)}`).then((r) => setOrders(r.data.orders || [])).catch(() => setOrders([]));
  }, [status, q]);
  useEffect(() => {
    const loadSummary = () => api.get("/marketplace/seller/orders/summary").then((r) => setSummary(r.data.summary || {})).catch(() => {});
    loadSummary();
    const id = setInterval(loadSummary, 30000);
    return () => clearInterval(id);
  }, [orders]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="seller-orders-page">
      {manualOpen && <ManualOrderModal onClose={() => setManualOpen(false)} onDone={load} />}
      <div className="flex items-start justify-between gap-3">
        <PageHeader title="Orders" icon={Package} subtitle="Manage, process and ship the orders customers placed with your store." />
        <button onClick={() => setManualOpen(true)} data-testid="orders-add-manual" className="mt-1 inline-flex shrink-0 items-center gap-1.5 rounded-full brand-gradient-bg px-4 py-2 text-sm font-semibold text-white"><Plus className="h-4 w-4" /> Add order</button>
      </div>

      <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700" data-testid="seller-shipping-notice">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <span>You are responsible for packing, courier selection, dispatch, shipment handover and accurate tracking information. Suffle provides the management platform but does not physically ship your parcels.</span>
      </div>

      {custom.length > 0 && (
        <div className="mb-6 rounded-2xl border border-violet-200 bg-violet-50/40 p-4" data-testid="seller-custom-orders">
          <h2 className="mb-1 text-sm font-bold text-violet-800">Custom Orders <span className="rounded-full bg-violet-200 px-2 py-0.5 text-xs">{custom.length}</span></h2>
          <p className="mb-3 text-xs text-violet-600">Confirmed &amp; paid made-to-order requests. Track fulfilment here alongside your regular orders.</p>
          <div className="space-y-3">
            {custom.map((r) => {
              const fs = r.fulfilment_status || "processing";
              const next = CUSTOM_NEXT[fs];
              return (
                <div key={r.request_id} className="rounded-xl border border-violet-100 bg-white p-3" data-testid={`custom-order-row-${r.request_id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-sm"><b className="text-slate-800">{r.number}</b> <span className="text-xs text-slate-400">· {r.product_name} · {r.customer_name || r.customer_email}</span></div>
                    <div className="flex items-center gap-2">
                      {r.unread > 0 && <span data-testid={`custom-unread-${r.request_id}`} className="rounded-full bg-rose-500 px-2 py-0.5 text-xs font-bold text-white">{r.unread} new</span>}
                      <span className="font-semibold text-slate-900">₹{r.quote_amount}</span>
                      <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-semibold capitalize text-violet-700" data-testid={`custom-fulfil-status-${r.request_id}`}>{fs}</span>
                    </div>
                  </div>
                  <p className="mt-2 whitespace-pre-wrap rounded-lg bg-slate-50 p-2 text-xs text-slate-600">{r.requirements}</p>
                  {r.files?.length > 0 && <div className="mt-1 flex flex-wrap gap-2 text-xs">{r.files.map((f, i) => <a key={i} href={f.url} target="_blank" rel="noreferrer noopener noreferrer" className="text-sky-600 underline">{f.name || `File ${i + 1}`}</a>)}</div>}
                  <div className="mt-2 flex items-center gap-1 text-[11px] text-slate-400">
                    {CUSTOM_FLOW.map((s, i) => <span key={s} className={CUSTOM_FLOW.indexOf(fs) >= i ? "font-semibold text-violet-600" : ""}>{s}{i < CUSTOM_FLOW.length - 1 ? " →" : ""} </span>)}
                  </div>
                  {next && (
                    <button onClick={() => advanceCustom(r)} data-testid={`custom-advance-${r.request_id}`} className="mt-2 rounded-full brand-gradient-bg px-3 py-1.5 text-xs font-semibold text-white">{CUSTOM_NEXT_LABEL[next]}</button>
                  )}
                  <button onClick={() => setDetail(r)} data-testid={`custom-detail-${r.request_id}`} className="ml-2 mt-2 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">View details</button>
                  {fs === "completed" && <span className="mt-2 inline-block text-xs font-semibold text-emerald-600" data-testid={`custom-completed-${r.request_id}`}>Completed ✓</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="seller-order-summary">
        {BUCKETS.map(([k, lbl]) => {
          const n = summary[k] ?? 0;
          const hot = HILITE_BUCKETS.includes(k) && n > 0;
          return (
            <button key={k} onClick={() => BUCKET_STATUS[k] && setStatus(BUCKET_STATUS[k])}
              className={`relative rounded-xl border p-3 text-left transition hover:shadow-sm ${hot ? "border-rose-300 bg-rose-50" : "border-slate-200 bg-white"}`} data-testid={`bucket-${k}`}>
              <p className={`text-2xl font-bold ${hot ? "text-rose-600" : "text-slate-900"}`}>{n}</p>
              <p className="mt-0.5 text-[11px] font-medium text-slate-500">{lbl}</p>
              {hot && <span className="absolute right-2 top-2 flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-75" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-rose-500" /></span>}
            </button>
          );
        })}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]"><Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search order #, product, customer, AWB…" className="pl-9" data-testid="seller-order-search" /></div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="seller-status-filter" className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
          {STATUS_OPTS.map((s) => <option key={s} value={s}>{s === "all" ? "All statuses" : label(s)}</option>)}
        </select>
      </div>

      {!orders ? null : orders.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="seller-orders-empty">
          <Package className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-3 font-semibold text-slate-700">No orders found</p>
          <p className="mt-1 text-sm text-slate-500">Paid orders from customers will appear here.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="seller-order-table">
            <thead className="border-b border-slate-100 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr><th className="p-3">Order #</th><th className="p-3">Date</th><th className="p-3">Customer</th><th className="p-3">Items</th><th className="p-3">Amount</th><th className="p-3">Status</th><th className="p-3">Courier / AWB</th><th className="p-3"></th></tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.suborder_id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`seller-order-row-${o.suborder_id}`}>
                  <td className="p-3"><span className="font-semibold text-slate-800">{o.number}</span><br /><span className="text-[11px] text-slate-400">{o.master_number}</span></td>
                  <td className="p-3 text-slate-500">{new Date(o.created_at).toLocaleDateString()}</td>
                  <td className="p-3 text-slate-700">{o.address?.name || "—"}</td>
                  <td className="p-3 text-slate-600">{o.items?.reduce((a, it) => a + it.qty, 0)} item(s)</td>
                  <td className="p-3 font-semibold text-slate-900">₹{o.subtotal}</td>
                  <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${badge(o.status)}`}>{label(o.status)}</span>{o.manual && <span className="ml-1 rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold text-indigo-700" data-testid={`order-manual-${o.suborder_id}`}>Manual</span>}</td>
                  <td className="p-3 text-slate-600">{o.shipment ? <span>{o.shipment.courier}<br /><span className="text-[11px] text-slate-400">{o.shipment.awb}</span>{typeof o.shipment.freight === "number" && o.shipment.freight > 0 && <span className="block text-[11px] text-slate-500">Freight ₹{o.shipment.freight}</span>}</span> : (
                    est[o.suborder_id]?.loading ? <span className="text-[11px] text-slate-400">Estimating…</span>
                    : est[o.suborder_id]?.fwd != null ? <span className="text-[11px] text-slate-600">Fwd ₹{est[o.suborder_id].fwd} · Ret ₹{est[o.suborder_id].ret}</span>
                    : est[o.suborder_id]?.none ? <span className="text-[11px] text-rose-400">No courier</span>
                    : est[o.suborder_id]?.error ? <span className="text-[11px] text-slate-400">{est[o.suborder_id].error}</span>
                    : <button onClick={() => estimate(o.suborder_id)} data-testid={`estimate-${o.suborder_id}`} className="text-[11px] font-semibold text-blue-600 underline">Estimate freight</button>
                  )}</td>
                  <td className="p-3"><div className="flex justify-end gap-2">
                    {SHIPPABLE.includes(o.status) && !o.shipment && (
                      <button onClick={() => openShip(o)} data-testid={`ship-${o.suborder_id}`} className="inline-flex items-center gap-1 rounded-full border border-blue-300 bg-white px-3 py-1.5 text-xs font-semibold text-blue-600 hover:bg-blue-50"><Truck className="h-3.5 w-3.5" /> Ship</button>
                    )}
                    <button onClick={() => downloadFile(`/marketplace/orders/${o.suborder_id}/tax-invoice.pdf`, `${o.number}-tax-invoice.pdf`)} data-testid={`invoice-${o.suborder_id}`} className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">Invoice</button>
                    {["returned", "refunded", "cancelled"].includes(o.status) && <button onClick={() => downloadFile(`/marketplace/refunds/${o.suborder_id}/credit-note.pdf`, `${o.number}-credit-note.pdf`)} data-testid={`creditnote-${o.suborder_id}`} className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">Credit note</button>}
                    <button onClick={() => nav(`/agent/orders/${o.suborder_id}`)} data-testid={`manage-${o.suborder_id}`} className="rounded-full brand-gradient-bg px-3 py-1.5 text-xs font-semibold text-white">Manage</button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {ship && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !ship.busy && setShip(null)} data-testid="quick-ship-modal">
          <div className="w-full max-w-md rounded-2xl bg-white p-5" onClick={(e) => e.stopPropagation()}>
            <div className="mb-1 flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-700">Ship order {ship.sub.number}</span>
              <button onClick={() => !ship.busy && setShip(null)} className="rounded-full p-1 text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
            </div>
            <p className="mb-3 text-xs text-slate-500">Get live courier rates and ship via Shiprocket — the tracking number is generated automatically and emailed to the customer. Forward freight is deducted from your settlement.</p>
            <div className="mb-3 grid grid-cols-4 gap-2">
              {[["weight", "Wt kg"], ["length", "L cm"], ["breadth", "B cm"], ["height", "H cm"]].map(([k, lbl]) => (
                <div key={k}><label className="text-[11px] text-slate-500">{lbl}</label>
                  <Input type="number" value={ship.parcel[k]} onChange={(e) => setShip((s) => ({ ...s, parcel: { ...s.parcel, [k]: e.target.value } }))} className="mt-1 h-9" data-testid={`qs-${k}`} /></div>
              ))}
            </div>
            <button onClick={shipRates} disabled={ship.loading} data-testid="qs-get-rates" className="rounded-full border border-blue-300 bg-white px-4 py-2 text-xs font-semibold text-blue-600 disabled:opacity-50">{ship.loading ? "Fetching rates…" : "Get courier rates"}</button>
            {ship.rates && (
              <div className="mt-3 max-h-48 space-y-2 overflow-y-auto">
                {ship.rates.length === 0 && <p className="text-xs text-rose-500">No couriers available for this route/parcel.</p>}
                {ship.rates.map((c) => (
                  <label key={c.courier_id} className={`flex cursor-pointer items-center justify-between rounded-lg border px-3 py-2 text-sm ${ship.sel === c.courier_id ? "border-blue-400 bg-blue-50" : "border-slate-200"}`} data-testid={`qs-courier-${c.courier_id}`}>
                    <span className="flex items-center gap-2"><input type="radio" checked={ship.sel === c.courier_id} onChange={() => setShip((s) => ({ ...s, sel: c.courier_id }))} /> <b className="inline-flex items-center gap-2"><CourierLogo name={c.courier_name} />{c.courier_name}</b></span>
                    <span className="text-xs text-slate-600">₹{c.forward_charge}</span>
                  </label>
                ))}
              </div>
            )}
            {ship.rates && ship.rates.length > 0 && (
              <button onClick={doShip} disabled={ship.busy || !ship.sel} data-testid="qs-confirm-ship" className="mt-4 w-full rounded-full brand-gradient-bg px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50">{ship.busy ? "Shipping…" : `Confirm & Ship${ship.sel ? ` (₹${shipSel.forward_charge})` : ""}`}</button>
            )}
          </div>
        </div>
      )}

      {detail && (
        <CustomOrderDetail request={detail} role="seller" onClose={() => setDetail(null)}
          onReload={async () => { const orders = await loadCustom(); setDetail((d) => d ? (orders.find((o) => o.request_id === d.request_id) || d) : d); }} />
      )}
    </div>
  );
}
